import { Modal } from 'almanac-ui/Modal'
import { Button } from 'blaze-ui/Button'
import { Text } from 'blaze-ui/Text/Text'
import { LineSkeleton } from 'components/Skeleton/LineSkeleton'
import { useAudioSample } from 'hooks/useAudioSample'
import { AvatarCard } from 'screens/ContentPreferences/tabs/AvatarsTab/AvatarCard'
import { AvatarDraft, avatarFromDraft } from '../types'
import Styles from '../AiAvatarAnnouncementModal.module.scss'

interface Props {
  drafts: AvatarDraft[]
  onDraftsChange: (drafts: AvatarDraft[]) => void
  onAddAvatar: () => void
  onEditAvatar: (draft: AvatarDraft) => void
  onBack: () => void
  onContinue: () => void
  isCreating: boolean
  isLoading: boolean
}

const SKELETON_KEYS = ['a', 'b', 'c']

const SkeletonCard = () => (
  <div className={Styles.skeletonCard} data-testid="avatar-skeleton-card">
    <LineSkeleton className={Styles.skeletonPoster} />
    <div className={Styles.skeletonInfo}>
      <LineSkeleton className={Styles.skeletonLine} width="60%" />
      <LineSkeleton className={Styles.skeletonLine} width="90%" />
    </div>
  </div>
)

export const ChooseAvatarsStep = ({
  drafts,
  onDraftsChange,
  onAddAvatar,
  onEditAvatar,
  onBack,
  onContinue,
  isCreating,
  isLoading,
}: Props) => {
  const { playingKey, toggle } = useAudioSample()

  const handleRemove = (localId: string) => onDraftsChange(drafts.filter((draft) => draft.localId !== localId))

  const showSkeletons = isLoading && drafts.length === 0

  return (
    <Modal.Root size="md">
      <Modal.Header
        title="Avatars matched to your Brand Kit"
        subHeader={
          <Text variant="secondary" color="var(--dark-60)">
            Recommended for your brand. Click one to preview, hover to remove, or add your own.
          </Text>
        }
      />
      <Modal.Content>
        <div className={Styles.grid}>
          {showSkeletons
            ? SKELETON_KEYS.map((key) => <SkeletonCard key={key} />)
            : drafts.map((draft, index) => (
                <AvatarCard
                  key={draft.localId}
                  avatar={avatarFromDraft(draft, index)}
                  isPreviewPlaying={playingKey === draft.localId}
                  showVerification={false}
                  onTogglePreview={() => toggle(draft.localId, draft.voiceSampleUrl ?? null)}
                  onEdit={() => onEditAvatar(draft)}
                  onDelete={() => handleRemove(draft.localId)}
                />
              ))}
        </div>
        <Button variant="secondary" size="md" isDisabled={showSkeletons} onPress={onAddAvatar}>
          + Add Avatar
        </Button>
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Button variant="tertiary" size="lg" onPress={onBack}>
            Back
          </Button>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <Button
            variant="primary"
            size="lg"
            isDisabled={drafts.length < 1 || isCreating || isLoading}
            onPress={onContinue}
          >
            Continue With {drafts.length} {drafts.length === 1 ? 'Avatar' : 'Avatars'}
          </Button>
        </Modal.FooterContent>
      </Modal.Footer>
    </Modal.Root>
  )
}
